import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import placeholder from '#src/assets/players/placeholder.webp';
import { FullViewportScroll } from '#src/atoms/container/FullViewportScroll/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { FullWidthContainer } from '#src/atoms/layout/FullWidthContainer/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LinkBox, LinkOverlay } from '#src/atoms/navigation/Box/index.tsx';
import { Tooltip } from '#src/atoms/tooltip/Tooltip/index.tsx';
import { Bold } from '#src/atoms/typography/Bold/index.tsx';
import { HeadlineXL, LabelM } from '#src/atoms/typography/index.tsx';
import { Chip } from '#src/atoms/ui/Chip/index.tsx';
import { proxyUrl } from '#src/atoms/ui/ResponsiveImg/index.tsx';
import { Follower } from '#src/components/player/PlayerPage/Header/Follower/index.tsx';
import type { MetricProps } from '#src/components/seo/HeaderMetrics/index.tsx';
import { HeaderMetrics } from '#src/components/seo/HeaderMetrics/index.tsx';
import { useIsLoggedIn } from '#src/hooks/auth/useIsLoggedIn.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useViewItem } from '#src/hooks/events/useViewItem.ts';
import { glossary } from '#src/lib/glossary.ts';
import { laptopAndAbove, tabletAndAbove } from '#src/style/mediaQuery.ts';
import { createVar } from '#src/style/utils.tsx';

import type {
  Header_dataPartner,
  Header_subscription,
} from './__generated__/index.graphql.ts';

const backgroundImgVar = createVar();
const backgroundColorVar = createVar();

const Root = styled.div`
  position: relative;
  isolation: isolate;
  background-image: linear-gradient(
    to bottom,
    var(${backgroundColorVar}) 0%,
    transparent 100%
  );
`;
const StyledFullWidthContainer = styled(FullWidthContainer)`
  --container-padding: var(--double-unit);
`;
const Picture = styled.div`
  width: 100%;
  height: 450px;
  position: relative;
  &::before {
    position: absolute;
    inset: 0;
    z-index: 0;
    background: var(${backgroundImgVar}) no-repeat;
    background-position: center var(--unit);
    background-size: 600px;
    mask-image: linear-gradient(to top, transparent 0%, black 40%);
    content: '';

    @media ${laptopAndAbove} {
      background-position: right calc(8 * var(--unit)) top var(--unit);
    }
  }

  .team & {
    --image-size: 120px;
    --padding-top: calc(6 * var(--unit));
    height: calc(var(--image-size) + var(--padding-top));
    &::before {
      background-size: auto var(--image-size);
      mask-image: initial;
      background-position: var(--double-unit) bottom;

      @media ${laptopAndAbove} {
        background-position: var(--double-unit) bottom;
      }
    }
  }
`;
const Subscription = styled(Horizontal)`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

const UpcomingGame = styled.div`
  width: 100%;
  position: sticky;
  bottom: 0;
  transition: 0.3s ease bottom;
  background: var(--c-black);
  padding: var(--double-unit);
  border-top: 1px solid var(--c-nd-200);

  @media ${laptopAndAbove} {
    position: absolute;
    border-top: none;
    background: transparent;
    bottom: var(--double-unit);
    width: calc(600px - var(--unit)); /* aligned with the picture background */
    padding-left: 0;
    padding-right: 0;
    right: 40px;

    & > div {
      margin: 0 auto;
    }

    /* Disable pointer events on this block to avoid covering clickable elements */
    pointer-events: none;
    /* Re-enable for buttons and links */
    & :is(a, button) {
      pointer-events: auto;
    }
  }
`;

const Content = styled(Vertical)`
  position: absolute;
  inset: auto 0 0 0;
  justify-content: end;

  @media ${laptopAndAbove} {
    padding-bottom: var(--triple-unit);
  }

  .team & {
    position: initial;
  }
`;
const StyledHeadlineXL = styled(HeadlineXL)`
  font-size: 48px;
  line-height: 1;

  &.small {
    font-size: 40px;
  }
  &.smaller {
    font-size: 32px;
  }

  @media ${laptopAndAbove} {
    .player & {
      max-width: 800px;
    }

    font-size: 120px;
    &.small {
      font-size: 110px;
    }
    &.smaller {
      font-size: 100px;
    }
  }
`;
const Memberships = styled(FullViewportScroll)`
  overflow-x: auto;
  padding-bottom: var(--half-unit);
  gap: var(--unit);

  @media ${tabletAndAbove} {
    padding-bottom: 0;
  }
`;
const MembershipImg = styled.img`
  width: auto;
  height: var(--double-unit);
`;

type Props = {
  id: string;
  variant?: 'player' | 'team';
  pictureUrl: string | null;
  displayName: string;
  color?: string | null;
  memberships?: {
    name: string;
    pictureUrl?: string | null;
    to?: string;
    verified?: boolean;
  }[];
  scores?: MetricProps[];
  injury?: MetricProps;
  subscription?: Header_subscription;
  dataPartner?: Header_dataPartner | null;
  upcomingGame?: ReactNode;
  addToWatchlistNode?: ReactNode;
};

export const Header = ({
  id,
  variant = 'player',
  pictureUrl,
  displayName,
  color,
  memberships,
  scores = [],
  injury,
  subscription,
  dataPartner,
  upcomingGame,
  addToWatchlistNode,
}: Props) => {
  const isLoggedIn = useIsLoggedIn();
  useViewItem({ id });
  const metrics = [...scores];
  const isLaptop = useIsLaptop();
  if (injury) metrics.push(injury);
  const upcomingGameElement = upcomingGame && (
    <UpcomingGame>{upcomingGame}</UpcomingGame>
  );

  const showFollower = subscription && (isLoggedIn || !upcomingGameElement);
  return (
    <Root
      className={classNames(variant)}
      style={{
        [backgroundColorVar]: color,
      }}
    >
      <Picture
        style={{
          [backgroundImgVar]: `url('${proxyUrl(pictureUrl || placeholder, {
            cropWidth: 480,
          })}')`,
        }}
      />

      {!!(addToWatchlistNode || showFollower) && (
        <Subscription>
          {!!showFollower && (
            <Follower subscription={subscription} dataPartner={dataPartner} />
          )}
          {addToWatchlistNode}
        </Subscription>
      )}

      {!!isLaptop && upcomingGameElement}
      {!isLaptop &&
        !isLoggedIn &&
        createPortal(upcomingGameElement, document.body)}
      <Content>
        <StyledFullWidthContainer>
          <Vertical gap={1.5}>
            <StyledHeadlineXL
              as="h1"
              className={classNames({
                small: displayName.length >= 20,
                smaller: displayName.length >= 40,
              })}
            >
              {displayName}
            </StyledHeadlineXL>
            {!!memberships && (
              <Memberships>
                {memberships.map(
                  ({
                    name,
                    pictureUrl: membershipPictureUrl,
                    to,
                    verified,
                  }) => (
                    <LinkBox key={name}>
                      <Chip size="medium">
                        <>
                          {!!membershipPictureUrl && (
                            <MembershipImg src={membershipPictureUrl} alt="" />
                          )}
                          {to ? (
                            <LinkOverlay as={Link} to={to}>
                              <Horizontal center gap={0.5}>
                                {name}
                                {!!verified && (
                                  <Tooltip
                                    title={
                                      <LabelM as="p">
                                        <FormattedMessage
                                          {...glossary.officiallyLicensed}
                                          values={{
                                            displayName: name,
                                            b: Bold,
                                          }}
                                        />
                                      </LabelM>
                                    }
                                    placement="top"
                                  >
                                    <FontAwesomeIcon icon={faBadgeCheck} />
                                  </Tooltip>
                                )}
                              </Horizontal>
                            </LinkOverlay>
                          ) : (
                            name
                          )}
                        </>
                      </Chip>
                    </LinkBox>
                  )
                )}
              </Memberships>
            )}
            <HeaderMetrics metrics={metrics} />
          </Vertical>
        </StyledFullWidthContainer>
      </Content>
    </Root>
  );
};

Header.fragments = {
  subscription: gql`
    fragment Header_subscription on WithSubscriptionsInterface {
      slug
      subscriptionsCount
      currentUserSubscription {
        slug
      }
      ...Follower_subscription
    }
    ${Follower.fragments.subscription}
  ` as TypedDocumentNode<Header_subscription>,
  dataPartner: gql`
    fragment Header_dataPartner on DataPartner {
      slug
      ...Follower_dataPartner
    }
    ${Follower.fragments.dataPartner}
  ` as TypedDocumentNode<Header_dataPartner>,
};
